import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ConfirmModalComponent } from '../../../../modals/confirm-modal/confirm-modal.component';
import { FirebaseService } from '../../../../service/firebase.service';
import { Device, DataForCharts } from '../../statistics.model';
import { StatisticsService } from '../../statistics.service';
import { TranslationPipe } from '../../../../shared/pipes/translation.pipe';
import { ModalMainComponent } from '../../../../modals/modal-main/modal-main.component';
import { Location } from '@angular/common';


@Component({
  selector: 'app-device',
  templateUrl: './device.component.html',
  styleUrls: ['./device.component.scss']
})
export class DeviceComponent implements OnInit {

  _deviceFromRoute: Device;
  chartsData: DataForCharts[] = [];
  deviceDatas: Device;
  _filteredDevice: Device[];
  currentDevice: Device;

  mediaContentTableColumns: string[] = ['name', 'type', 'views', 'lifecycleStart', 'lifecycleEnd', 'actions'];

  view: any[] = [700, 400];

  // options
  showXAxis = true;
  showYAxis = true;
  gradient = false;

  showXAxisLabel = true;
  xAxisLabel = "Nom de l'appareil";
  showYAxisLabel = true;
  yAxisLabel = 'Nombre de vues';

  showLabels: boolean = true;
  isDoughnut: boolean = false;
  legendPosition: string = 'right';

  colorScheme = {
    domain: ['#122B47', '#3A5DE5', '#EE2E4F', '#FCBF00']
  };

  // One entry per Firestore document matching the device name
  devicesId: string[] = [];
  mediasData: any;

  parsedMediaData: any;
  startDateRange: Date;
  endDateRange: Date;

  defaultStartDateRange: Date;
  defaultEndDateRange: Date;
  isMediaLicenceEnable: boolean = false;
  deviceStatsData: any;
  sessionStatsData: any[] = [];
  mediasStatsData: any[] = [];
  isInit: boolean = false;
  isLoading: boolean = true;
  disabledDelete: boolean = false;
  private dialogRef: MatDialogRef<any>;

  constructor(
    private route: ActivatedRoute,
    private service: StatisticsService,
    private firebaseService: FirebaseService,
    private _ref: ChangeDetectorRef,
    private _router: Router,
    private dialog: MatDialog,
    public translation: TranslationPipe,
    private location: Location
  ) {
    this.endDateRange = new Date();
    this.endDateRange.setHours(23, 59, 0, 0);

    this.startDateRange = new Date();
    this.startDateRange.setMonth(this.endDateRange.getMonth() - 1);
    this.startDateRange.setHours(0, 1, 0, 0);

    this.defaultStartDateRange = new Date(this.startDateRange.getTime());
    this.defaultEndDateRange = new Date(this.endDateRange.getTime());
  }

  ngOnInit(): void {
    this.route.params.subscribe((params: Params) => {
      this._deviceFromRoute = {
        name: this.route.snapshot.params['id']
      };
      this.onRouteSub();
    });

    if (this.firebaseService.user.licence.find(v => v.type == 'media'))
      this.isMediaLicenceEnable = true;
  }

  refreshStats() {
    this.initData();
  }

  triggerStatsSuppression() {
    this.dialogRef = this.dialog.open(ConfirmModalComponent, {
      width: '70vw',
      height: '30vh',
      maxWidth: '500px',
      data: {
        title: this.translation.transform('warningDeleteStats')
      }
    });

    this.dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.disabledDelete = true;
        this.isLoading = true;
        // NOTE: make sure deleteAllDeviceStats iterates over the whole array
        this.firebaseService.deleteAllDeviceStats(this.devicesId).then(() => {
          this.disabledDelete = false;
          this.isLoading = false;
          this._router.navigate(['/statistics']);
        });
      }
    });
  }

  editDeviceName() {
    this.dialogRef = this.dialog.open(ModalMainComponent, {
      width: '30vw',
      height: '30vh',
      data: {
        title: "Veuillez entrer le nouveau nom de la borne.",
        type: 'text',
        buttonLabel: this.translation.transform('validate'),
        buttonColor: 'accent',
        currentValue: this.deviceDatas.deviceName
      }
    });

    this.dialogRef.afterClosed().subscribe(result => {
      const newName = result?.currentValue;

      if (newName && newName.length > 0 && newName != this.deviceDatas.deviceName) {
        // Rename every document that belongs to this device
        Promise.all(
          this.devicesId.map(id =>
            this.firebaseService.updateDeviceStatsData(id, { deviceName: newName })
          )
        ).then(() => {
          this.deviceDatas.deviceName = newName;
        });
      }
    });
  }

  setDateRange() {
    this.endDateRange.setHours(23, 59, 0, 0);
    this.startDateRange.setHours(0, 1, 0, 0);

    if (this.startDateRange != null && this.endDateRange != null && this.isInit &&
      (this.startDateRange.getTime() != this.defaultStartDateRange.getTime() ||
        this.endDateRange.getTime() != this.defaultEndDateRange.getTime())) {
      this.initData();

      this.defaultEndDateRange = new Date(this.endDateRange.getTime());
      this.defaultStartDateRange = new Date(this.startDateRange.getTime());
    }
  }

  resetDate() {
    this.endDateRange = new Date();
    this.endDateRange.setHours(23, 59, 0, 0);

    this.startDateRange = new Date();
    this.startDateRange.setMonth(this.endDateRange.getMonth() - 1);
    this.startDateRange.setHours(0, 1, 0, 0);

    this.initData();
  }

  async initData() {
    this.isLoading = true;
    this.chartsData = [];
    this.devicesId = [];

    const deviceName = this.route.snapshot.params['id'];

    try {
      const deviceSnap = await this.firebaseService.getDeviceStat(deviceName);
      this.deviceStatsData = deviceSnap;

      if (deviceSnap.empty) {
        this.isLoading = false;
        return;
      }

      this.devicesId = deviceSnap.docs.map(d => d.id);

      // One call per device document, in parallel
      const [sessionSnaps, mediaSnaps] = await Promise.all([
        Promise.all(this.devicesId.map(id =>
          this.firebaseService.getDeviceSessionStat(id, this.startDateRange, this.endDateRange))),
        Promise.all(this.devicesId.map(id =>
          this.firebaseService.getDeviceMediaStat(id, this.startDateRange, this.endDateRange)))
      ]);

      this.sessionStatsData = sessionSnaps;
      this.mediasStatsData = mediaSnaps;

      // ---- Merge the device documents into a single Device ----
      const allDevices: any[] = deviceSnap.docs.map(d => d.data());
      const merged: any = { ...allDevices[0] };

      merged.sessions = sessionSnaps.reduce((sum, s) => sum + s.docs.length, 0);

      const created = allDevices.map(d => d.createdAt?.toDate?.()).filter(Boolean) as Date[];
      const latest = allDevices.map(d => d.latestSession?.toDate?.()).filter(Boolean) as Date[];
      if (created.length) merged.createdAt = new Date(Math.min(...created.map(d => d.getTime())));
      if (latest.length) merged.latestSession = new Date(Math.max(...latest.map(d => d.getTime())));

      this.deviceDatas = merged;

      // ---- Concatenate media docs from every device and count per idMedia ----
      const counts = new Map<string, any>();
      this.mediasData = [];
      mediaSnaps.forEach(snap => snap.docs.forEach(doc => {
        const item: any = doc.data();
        this.mediasData.push(item);

        const entry = counts.get(item.idMedia);
        if (entry) entry.count++;
        else counts.set(item.idMedia, { ...item, count: 1 });
      }));

      // ---- Resolve media names/lifecycle (also detects deleted medias) ----
      const fmt = (t: any) => t?.toDate ? t.toDate().toLocaleDateString('fr-FR') : 'Aucune';

      const resolved = await Promise.all([...counts.values()].map(async item => {
        const snap: any = await this.firebaseService.getMedia(item.idMedia);
        const data = snap.data();

        if (!data || (data.displayName == undefined && data.name == undefined)) {
          // Media no longer exists -> clean up its stats
          this.firebaseService.deleteStatFromIdMedia(deviceName, item.idMedia);
          return null;
        }

        item.name = data.displayName || data.name;
        item.type = this.translation.transform(data.type) as string;
        item.lifecycle = { start: fmt(data.lifecycle?.start), end: fmt(data.lifecycle?.end) };
        return item;
      }));

      const medias = resolved.filter(Boolean);
      this.deviceDatas.medias = medias;

      this.chartsData = medias.map(m => ({ name: m.name, value: m.count }));
      this.isInit = true;
    } catch (err) {
      console.error(err);
    } finally {
      this.isLoading = false;
    }
  }

  removeStat(elem) {
    this.dialogRef = this.dialog.open(ConfirmModalComponent, {
      width: '30vw',
      height: '30vh',
      data: {
        title: this.translation.transform('warningApplyToAll'),
        ok: 'OK',
        cancel: this.translation.transform('cancel')
      }
    });

    this.dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // TODO: re-enable once the delete is ready, looping over this.devicesId
        // Promise.all(this.devicesId.map(id =>
        //   this.firebaseService.deleteStatFromMediaId(elem.idMedia, id)
        // )).then(() => this.initData());
      }
    });
  }

  onRouteSub() {
    this.initData();
  }

}
